import { parseAgentSessionKey, isSubagentSessionKey } from "../../../../src/routing/session-key.js";
import { cardsFromSpawnedSessions, mergeRestoredSubagents } from "../controllers/subagents.ts";
import { idbSaveSubagentStash } from "../storage/idb-session-cache.ts";
import type { SessionsListResult } from "../types.ts";
import type { ChatSubagentCard, ChatSubagentStatus } from "../ui-types.ts";
import type { ChatSessionContextMeta } from "../views/chat-session-context.ts";

const storeByHost = new WeakMap<object, Map<string, ChatSubagentCard[]>>();
const childIndexByHost = new WeakMap<
  object,
  Map<string, { parentKey: string; card: ChatSubagentCard }>
>();

/**
 * How many parent sessions keep a resident subagent stash.
 *
 * The `WeakMap` keyed by host bounds the LIFETIME of each map (dropped with
 * the host), but nothing bounded its SIZE: every session the operator
 * visited added an entry, and the only clearing path was "Reset Node". Each
 * entry holds full card objects including `streamText` (now capped, but
 * still the largest field on a card) and is mirrored into IndexedDB.
 *
 * 16 is generous for a shift — an operator rarely has more than a handful of
 * parent sessions with live subagents — while keeping the residue bounded.
 */
const SUBAGENT_STASH_MAX_SESSIONS = 16;

function evictStash(map: Map<string, ChatSubagentCard[]>): void {
  while (map.size > SUBAGENT_STASH_MAX_SESSIONS) {
    const oldest = map.keys().next().value;
    if (typeof oldest !== "string") {
      break;
    }
    map.delete(oldest);
  }
}

function storeFor(host: object): Map<string, ChatSubagentCard[]> {
  let map = storeByHost.get(host);
  if (!map) {
    map = new Map();
    storeByHost.set(host, map);
  }
  return map;
}

function childIndexFor(host: object): Map<string, { parentKey: string; card: ChatSubagentCard }> {
  let map = childIndexByHost.get(host);
  if (!map) {
    map = new Map();
    childIndexByHost.set(host, map);
  }
  return map;
}

function rebuildChildIndex(host: object, parentKey: string, cards: ChatSubagentCard[]): void {
  const index = childIndexFor(host);
  for (const [childKey, entry] of index) {
    if (entry.parentKey === parentKey) {
      index.delete(childKey);
    }
  }
  for (const card of cards) {
    const childKey = card.childSessionKey?.trim();
    if (childKey) {
      index.set(childKey, { parentKey, card });
    }
  }
}

export type SubagentCardLookup = {
  card: ChatSubagentCard;
  parentKey: string;
};

function cardFromSessionRow(
  row: NonNullable<SessionsListResult["sessions"]>[number],
  parentKey: string,
): ChatSubagentCard {
  const key = row.key ?? "";
  const parsed = parseAgentSessionKey(key);
  // JAISIU-fleet-map-idle-demote — only inherit the row's terminal
  // status when the row is in a settled state. For rows that look
  // in-flight (`running` / `queued` / `spawning`) we leave the
  // status as the honest "running" because the post-restart view
  // is going to demote it via `resolveSubagentActivityPhase` anyway
  // (no runId + no stream text + stale → "idle" → Fleet Map badge
  // shows "Idle"). Mapping `done` / `error` / `cancelled` straight
  // through means the table, the badge, and the active flag all
  // agree on terminal truth without a phase-detour.
  const rowStatus = row.status;
  const inheritedStatus: ChatSubagentStatus =
    rowStatus === "done" || rowStatus === "error" || rowStatus === "cancelled"
      ? rowStatus
      : "running";
  return {
    id: `restore-${key}`,
    runId: "",
    childSessionKey: key,
    label: row.label ?? row.displayName ?? "Subagent",
    agentId: parsed?.agentId ?? "main",
    task: row.lastMessagePreview ?? "",
    status: inheritedStatus,
    createdAt: row.updatedAt ?? Date.now(),
  };
}

/** Persist parallel subagent cards for a parent session when switching away. */
/**
 * Persist parallel subagent cards for a parent session when switching away.
 *
 * Note on the empty-list guard: we only short-circuit on a missing
 * sessionKey. Stashing an empty list is intentional — it lets a later
 * caller distinguish "we saw this session and it had no subagents"
 * from "we never visited this session". The catalog merge in
 * mergeRestoredSubagents handles empty stashes correctly.
 */
export function stashChatSubagentsForSession(
  host: object,
  sessionKey: string,
  cards: ChatSubagentCard[],
) {
  const key = sessionKey.trim();
  if (!key) {
    return;
  }
  if (isSubagentSessionKey(key) && !cards.some((c) => c.childSessionKey?.trim())) {
    return;
  }
  const store = storeFor(host);
  // Never let an empty stash destroy a populated one for the same key.
  //
  // A session switch stashes twice: the heavy path in `app.ts` saves the
  // outgoing cards, then `activateChatSessionTab` saves `host.sessionKey`
  // (still the OUTGOING key) against `host.chatSubagents`. If anything had
  // cleared that field in between, the second call wrote `[]` over the real
  // cards for the same key — and because this function overwrites
  // unconditionally (and mirrors to IndexedDB), the outgoing session's
  // subagent cards were gone from memory and disk, so the inspector came
  // back empty on the way back.
  //
  // Both production callers pass `host.chatSubagents ?? []` with the intent
  // "save what is on screen" — neither ever means "forget this session", so
  // dropping an empty overwrite cannot lose anything the caller wanted.
  if (cards.length === 0) {
    const existing = store.get(key);
    if (existing && existing.length > 0) {
      return;
    }
  }
  const copy = cards.map((c) => ({ ...c }));
  // Re-insert so iteration order is recency-ordered for `evictStash`.
  store.delete(key);
  store.set(key, copy);
  evictStash(store);
  rebuildChildIndex(host, key, copy);
  // JAISIU-3320 / Pillar I — mirror to IndexedDB so the stash survives
  // a hard reload. Fire-and-forget: IDB failures must never block the
  // synchronous tab switch path. No-op when IDB is unavailable.
  void idbSaveSubagentStash(key, copy);
}

export function restoreChatSubagentsForSession(
  host: object,
  sessionKey: string,
  sessions: SessionsListResult | null,
): ChatSubagentCard[] {
  const key = sessionKey.trim();
  if (!key) {
    return [];
  }
  if (isSubagentSessionKey(key)) {
    const cached = storeFor(host).get(key) ?? [];
    return mergeRestoredSubagents(key, sessions, cached);
  }
  const cached = storeFor(host).get(key) ?? [];
  return mergeRestoredSubagents(key, sessions, cached);
}

export function lookupSubagentCard(
  host: object,
  childSessionKey: string,
  sessions: SessionsListResult | null,
  tabMeta?: Record<string, ChatSessionContextMeta>,
): SubagentCardLookup | null {
  const child = childSessionKey.trim();
  if (!child) {
    return null;
  }

  const indexed = childIndexFor(host).get(child);
  if (indexed) {
    return { card: { ...indexed.card }, parentKey: indexed.parentKey };
  }

  for (const [parentKey, cards] of storeFor(host)) {
    const found = cards.find((c) => c.childSessionKey === child);
    if (found) {
      return { card: { ...found }, parentKey };
    }
  }

  const metaParent = tabMeta?.[child]?.parentKey?.trim();
  if (metaParent) {
    const fromSpawn = cardsFromSpawnedSessions(metaParent, sessions).find(
      (c) => c.childSessionKey === child,
    );
    if (fromSpawn) {
      return { card: fromSpawn, parentKey: metaParent };
    }
  }

  const row = sessions?.sessions?.find((s) => s.key === child);
  const spawnedBy = row?.spawnedBy?.trim();
  if (spawnedBy) {
    const fromSpawn = cardsFromSpawnedSessions(spawnedBy, sessions).find(
      (c) => c.childSessionKey === child,
    );
    if (fromSpawn) {
      return { card: fromSpawn, parentKey: spawnedBy };
    }
    if (row) {
      return { card: cardFromSessionRow(row, spawnedBy), parentKey: spawnedBy };
    }
  }

  return null;
}

export function updateSubagentCardInStash(
  host: object,
  childSessionKey: string,
  updater: (card: ChatSubagentCard) => ChatSubagentCard,
): boolean {
  const child = childSessionKey.trim();
  if (!child) {
    return false;
  }
  const index = childIndexFor(host).get(child);
  const parentKey = index?.parentKey ?? lookupSubagentCard(host, child, null)?.parentKey;
  if (!parentKey) {
    return false;
  }
  const cards = storeFor(host).get(parentKey);
  if (!cards) {
    return false;
  }
  let touched = false;
  const next = cards.map((c) => {
    if (c.childSessionKey !== child) {
      return c;
    }
    touched = true;
    return updater(c);
  });
  if (!touched) {
    return false;
  }
  storeFor(host).set(parentKey, next);
  rebuildChildIndex(host, parentKey, next);
  // JAISIU-3320 / Pillar I — keep IDB in lockstep with the in-memory
  // stash after a card update. Fire-and-forget.
  void idbSaveSubagentStash(parentKey, next);
  return true;
}

/**
 * Flatten every stashed subagent card across all parent sessions. Used by
 * the right-side Fleet Inspector Subagents tab, which is fleet-wide rather
 * than per-chat. Caller is responsible for grouping/rendering; this helper
 * just merges. The order is most-recent-first within each parent (preserved
 * from stash insertion order). Active session cards are NOT included here —
 * callers should layer those on top if needed.
 */
export function collectAllStashedSubagents(
  host: object,
): Array<{ parentKey: string; cards: ChatSubagentCard[] }> {
  const store = storeByHost.get(host);
  if (!store) {
    return [];
  }
  const out: Array<{ parentKey: string; cards: ChatSubagentCard[] }> = [];
  for (const [parentKey, cards] of store) {
    if (cards.length === 0) {
      continue;
    }
    out.push({ parentKey, cards: cards.map((c) => ({ ...c })) });
  }
  return out;
}

/** Patch a subagent card on the active list and/or parent stash. */
export function patchSubagentCardEverywhere(
  host: object,
  params: { childSessionKey?: string; runId?: string },
  updater: (card: ChatSubagentCard) => ChatSubagentCard,
  activeCards: ChatSubagentCard[] | undefined,
  setActiveCards: (cards: ChatSubagentCard[]) => void,
): boolean {
  const child = params.childSessionKey?.trim();
  const runId = params.runId?.trim();
  let changed = false;

  if (activeCards?.length) {
    const next = activeCards.map((c) => {
      const match = (child && c.childSessionKey === child) || (runId && c.runId === runId);
      if (!match) {
        return c;
      }
      changed = true;
      return updater(c);
    });
    if (changed) {
      setActiveCards(next);
    }
  }

  if (child) {
    changed = updateSubagentCardInStash(host, child, updater) || changed;
  } else if (runId) {
    for (const [parentKey, cards] of storeFor(host)) {
      const has = cards.some((c) => c.runId === runId);
      if (!has) {
        continue;
      }
      const next = cards.map((c) => (c.runId === runId ? updater(c) : c));
      storeFor(host).set(parentKey, next);
      rebuildChildIndex(host, parentKey, next);
      // JAISIU-3320 / Pillar I — mirror IDB after a card update by runId.
      void idbSaveSubagentStash(parentKey, next);
      changed = true;
    }
  }

  return changed;
}

/**
 * Drop every stashed subagent card for this host. Used by Fleet Chat "Reset
 * Node" — after the server clears the subagent registry we must also clear
 * the per-session client-side cache, otherwise `restoreChatSubagentsForSession`
 * re-paints orphan cards the moment any tab is reactivated.
 *
 * Idempotent. Safe to call when the cache is empty.
 */
export function clearChatSubagentsForHost(host: object): void {
  const store = storeByHost.get(host);
  if (store) {
    store.clear();
  }
  const index = childIndexByHost.get(host);
  if (index) {
    index.clear();
  }
}
